import { useState, useEffect } from 'react';
import { APP_STATES } from '../constants';
import { hasActiveSession, getCurrentSession, isSessionExpired, maybeDevAutoLogin } from '../utils/authUtils';
import { isPWAInstalled } from '../utils/pwaUtils';
import { detectOS } from '../utils/deviceDetection';
import pb from '../pocketbase';

export const useAppState = () => {
  // Синхронная проверка сессии при инициализации компонента
  const initialSession = hasActiveSession() ? getCurrentSession() : null;
  const initialAppState = initialSession ? APP_STATES.MAIN : APP_STATES.LOADING;

  const [appState, setAppState] = useState(initialAppState);
  const [session, setSession] = useState(initialSession);
  const [isInitialized, setIsInitialized] = useState(!!initialSession);

  // Синхронная проверка кэшированной сессии при инициализации
  useEffect(() => {
    // Проверяем, есть ли активная сессия
    if (hasActiveSession()) {
      const currentSession = getCurrentSession();
      if (currentSession && !isSessionExpired(currentSession)) {
        // Если есть активная сессия, сразу переходим к основному приложению
        setSession(currentSession);
        setAppState(APP_STATES.MAIN);
        setIsInitialized(true);
        return;
      }
    }

    // Если нет активной сессии, помечаем как инициализированное для показа onboarding/login
    setIsInitialized(true);
  }, []);

  // Проверка авторизации и PWA (только если нет кэшированной сессии)
  useEffect(() => {
    // Если уже инициализированы с кэшированной сессией, не выполняем эту проверку
    if (isInitialized && session) {
      return;
    }

    // Если еще не инициализированы, ждем
    if (!isInitialized) {
      return;
    }

    let mounted = true;

    const checkAppState = async () => {
      try {
        // Проверяем PocketBase auth store
        if (!mounted) return;

        const currentSession = getCurrentSession();

        // Не перезаписываем сессию, если она уже установлена
        if (!session) {
          setSession(currentSession);
        }

        // Если пользователь уже залогинен, сразу переходим к основному приложению
        if (currentSession) {
          setAppState(APP_STATES.MAIN);
          return;
        }

        // Если пользователь не залогинен, пробуем dev автологин (только в dev)
        const didAutoLogin = await maybeDevAutoLogin();
        if (didAutoLogin) {
          if (!mounted) return;
          const freshSession = getCurrentSession();
          setSession(freshSession);
          if (freshSession) {
            setAppState(APP_STATES.MAIN);
            return;
          }
        }

        // Если пользователь всё ещё не залогинен, проверяем PWA и ОС
        const pwaInstalled = isPWAInstalled();
        const os = detectOS();
        const isMobileOS = os === 'iOS' || os === 'Android';

        if (!mounted) return;

        // Если не PWA и мобильная ОС (iOS или Android), отправляем на страницу установки
        if (!pwaInstalled && isMobileOS) {
          setAppState(APP_STATES.PWA_INSTALL);
        } else {
          // В остальных случаях (PWA или десктоп) отправляем на логин
          setAppState(APP_STATES.LOGIN);
        }
      } catch (error) {
        console.error('Error checking app state:', error);
        if (!mounted) return;
        // В случае ошибки показываем страницу входа
        setAppState(APP_STATES.LOGIN);
      }
    };

    checkAppState();

    // Слушаем изменения авторизации PocketBase
    const unsubscribe = pb.authStore.onChange((token, model) => {
      if (!mounted) return;

      const newSession = token && model ? { access_token: token, user: model } : null;
      setSession(newSession);

      // Если пользователь залогинился, сразу переходим к основному приложению
      if (newSession) {
        setAppState(APP_STATES.MAIN);
      } else {
        // Если пользователь разлогинился, проверяем PWA и ОС
        const pwaInstalled = isPWAInstalled();
        const os = detectOS();
        const isMobileOS = os === 'iOS' || os === 'Android';

        if (!pwaInstalled && isMobileOS) {
          setAppState(APP_STATES.PWA_INSTALL);
        } else {
          setAppState(APP_STATES.LOGIN);
        }
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [isInitialized, session]);

  return {
    appState,
    session,
    setAppState,
    setSession,
    isInitialized
  };
};
